import React, { useCallback, useEffect, useState } from 'react'
import { PageData } from '@/api/Pagination/Types'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { SubscriptionDetailDetailsDto } from 'api/Trading-Accounts/Types/SubscriptionDetailDetailsDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getSubscriptionFilterFields } from 'components/Filter/subscriptionsFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchTradingAccountSubscriptionClassificationTypesAction,
  fetchTradingAccountSubscriptionDetailTypesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { normalizeWalletsSortQuery } from '../../Records/Wallets/normalizer'
import { subscriptionColumns } from './subscriptionColumns'

const useMapStateToProps = () => ({
  gridState: useAppSelector((state) => state.grid),
  tradingAccountGroupsByTradingAccount: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountSubscriptionClassificationTypes
  ),
  tradingAccountSubscriptionClassificationTypes: useAppSelector(
    (state) => state.tradingAccounts.tradingAccountSubscriptionDetailTypes
  ),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),

    getTradingAccountSubscriptionClassificationTypes: async () =>
      dispatch(fetchTradingAccountSubscriptionClassificationTypesAction(tradingAccountsApiClient)),
    getTradingAccountSubscriptionDetailTypes: async () =>
      dispatch(fetchTradingAccountSubscriptionDetailTypesAction(tradingAccountsApiClient)),
  }
}

export const SubscriptionsPage: React.FC = () => {
  const crumbs = [{ title: 'Subscriptions', link: links.administrationSubscriptions.url }]
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [subscriptions, setSubscriptions] = useState<SubscriptionDetailDetailsDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { tradingAccountSubscriptionClassificationTypes, tradingAccountGroupsByTradingAccount } =
    useMapStateToProps()
  const {
    getTradingAccountSubscriptionClassificationTypes,
    getTradingAccountSubscriptionDetailTypes,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<SubscriptionDetailDetailsDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(tradingAccountSubscriptionClassificationTypes))
            await getTradingAccountSubscriptionClassificationTypes()
          if (isEmpty(tradingAccountGroupsByTradingAccount))
            await getTradingAccountSubscriptionDetailTypes()

          if (response?.items) {
            setSubscriptions(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<SubscriptionDetailDetailsDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeWalletsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setSubscriptions(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [getQuery, getSort, gridActions, gridState.pageSize, gridState.sort, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setSubscriptions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }
  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setSubscriptions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })
    const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setSubscriptions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getTradingAccountSubscriptionDetails({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setSubscriptions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.SubscriptionsOverview}
        filterFields={getSubscriptionFilterFields()}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={subscriptions} columns={subscriptionColumns}>
          <Grid
            {...gridState}
            resizable
            data={subscriptions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {subscriptionColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}
